import { api } from './api'

/**
 * Descarga de ficheros del servidor (los `.xlsx` de los paneles).
 *
 * Vive en un módulo aparte porque la descarga no es de un panel: la usan el de
 * dirección y el de marketing, y duplicarla habría significado dos copias de
 * unas reglas sutiles —cómo se saca el nombre del `Content-Disposition`, cómo
 * se lee el error de una respuesta que llegó como `Blob`— que con el tiempo
 * habrían dejado de coincidir.
 */

/** Quita de los params lo que está vacío: un `''` viajaría como parámetro presente. */
export function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

/**
 * Nombre del fichero según la cabecera `Content-Disposition`.
 *
 * El servidor es quien decide cómo se llama: el nombre va dentro del `.xlsx` y
 * duplicarlo en el frontend es dos sitios que pueden no coincidir. Si la cabecera
 * no viene, se usa el nombre de la ruta, que siempre existe.
 */
export function filenameFrom(disposition: string | undefined, fallback: string): string {
  const codificado = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '')?.[1]
  if (codificado !== undefined) return decodeURIComponent(codificado)

  const plano = /filename="?([^";]+)"?/i.exec(disposition ?? '')?.[1]

  return plano ?? `${fallback}.xlsx`
}

/**
 * Guarda un blob como fichero.
 *
 * El `URL.revokeObjectURL` va encolado y no al final: si se revoca antes de que el
 * navegador haya empezado a leer el blob —que es lo que pasa si se hace de
 * inmediato— la descarga llega vacía o no llega, y el fallo no se ve en consola.
 */
export function guardarBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const enlace = document.createElement('a')

  enlace.href = url
  enlace.download = filename
  enlace.rel = 'noopener'
  document.body.appendChild(enlace)
  enlace.click()
  document.body.removeChild(enlace)

  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * El mensaje de un error cuya respuesta es un blob.
 *
 * Con `responseType: 'blob'` el cuerpo de error llega como `Blob`, no como objeto:
 * sin leerlo, `error.response.data.error.message` es `undefined` y el toast
 * acabaría diciendo «Error interno del servidor» para un 403, que sí tiene
 * mensaje propio y es el que el usuario necesita leer.
 */
export async function mensajeDeBlob(data: unknown): Promise<string> {
  if (!(data instanceof Blob)) return 'No se pudo descargar el fichero.'

  try {
    const texto = await data.text()
    const parsed = JSON.parse(texto) as { error?: { message?: string } }

    return parsed.error?.message ?? 'No se pudo descargar el fichero.'
  } catch {
    return 'No se pudo descargar el fichero.'
  }
}

/**
 * Descarga una ruta del servidor como fichero.
 *
 * Va por axios y no con un `<a href>` a propósito. Un enlace del documento no
 * lleva la cabecera `Authorization`, así que llega sin token, el backend responde
 * 401 y el navegador se queda con un JSON de error en lugar del `.xlsx`. Por
 * axios el token lo pone el interceptor, y un 401 por token caducado se resuelve
 * solo: el interceptor refresca el token y reintenta.
 *
 * El rango viaja en la query porque el backend reutiliza el mismo request que el
 * panel: sin él, el `.xlsx` saldría con el mes actual aunque el usuario esté
 * mirando otro rango, y no habría forma de saber cuál de los dos es el bueno.
 */
export async function descargar(ruta: string, params: Record<string, unknown> = {}): Promise<void> {
  try {
    const respuesta = await api.get(ruta, {
      params: withoutEmpty(params),
      responseType: 'blob',
    })

    guardarBlob(
      respuesta.data as Blob,
      filenameFrom(
        respuesta.headers['content-disposition'] as string | undefined,
        ruta.split('/').pop() ?? 'export',
      ),
    )
  } catch (error) {
    const causa = error as { response?: { data?: unknown }; message?: string }

    throw new Error(await mensajeDeBlob(causa.response?.data))
  }
}
